import { useState, useCallback } from "react";
import moment from "moment";

/**
 * Hook to manage report filters (Weekly, Monthly, Custom Range)
 * Maintains backward compatibility with existing dashboard logic.
 * 
 * @param {Object} options 
 * @param {Array} options.initialRange Default range [start, end]
 * @param {Function} options.onFilterChange Callback when range changes
 * @param {boolean} options.useIsoWeek Whether to use ISO week for weekly report (UserDashboard style)
 */
const useReportFilter = ({ 
  initialRange = [moment().subtract(1, "week"), moment()], 
  onFilterChange = () => {},
  useIsoWeek = false 
} = {}) => {
  const [range, setRange] = useState(initialRange);
  const [activeFilter, setActiveFilter] = useState(1); // 1: Weekly, 2: Monthly, 3: Custom

  const setWeekly = useCallback(() => {
    const start = useIsoWeek ? moment().startOf("isoWeek") : moment().subtract(1, "week");
    const end = useIsoWeek ? moment().endOf("isoWeek") : moment();
    const newRange = [start, end];
    setRange(newRange);
    setActiveFilter(1);
    onFilterChange(1, start, end);
  }, [onFilterChange, useIsoWeek]);

  const setMonthly = useCallback(() => {
    const start = moment().date(1).startOf("day");
    const end = moment(start).endOf("month");
    const newRange = [start, end];
    setRange(newRange);
    setActiveFilter(2);
    onFilterChange(2, start, end);
  }, [onFilterChange]);

  const setCustom = useCallback((newRange) => {
    // Some dashboards use .date(1) and .endOf('month') for custom range if it's month-based
    // But usually it's just the range from the picker.
    // We'll allow the component to pass the processed range or handle it here if needed.
    setRange(newRange);
    setActiveFilter(3);
    onFilterChange(3, newRange[0], newRange[1]);
  }, [onFilterChange]);

  return {
    range,
    setRange, // Direct access for DateRangePicker
    activeFilter,
    setActiveFilter,
    setWeekly,
    setMonthly,
    setCustom
  };
};

export default useReportFilter;
